import { getCellId, rangeIndexToLabel } from "../../../components/process-design/cell/Utils";
import { unifyRanges } from "../../../utils/util";

function getCellIndexInMatrix(matrix, cellId) {
  for (let i = 0; i < matrix.length; i++) {
    for (let j = 0; j < matrix[i].length; j++) if (matrix[i][j] == cellId) return [i, j];
  }
  return [-1, -1];
}

const utils = (context) => {
  const { dispatch, current, rootState, rootGetters } = context;
  return {
    limitRangeWithinValidBound(range, activeCellIndex) {
      // Ensures the given value is constrained within the specified minimum and maximum bounds
      const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

      const maxColumn = current.design.columns.length - 1;
      const maxRow = current.design.rows.length - 1;

      const newRange = [
        clamp(range[0], 0, maxColumn),
        clamp(range[1], 0, maxRow),
        clamp(range[2], 0, maxColumn),
        clamp(range[3], 0, maxRow),
      ];

      const newActiveCellIndex = [clamp(activeCellIndex[0], 0, newRange[2]), clamp(activeCellIndex[1], 0, newRange[3])];

      return [newRange, newActiveCellIndex];
    },
    swapRangePosition(axis, position) {
      if (axis === "x" && position.includes("left")) position = position.replace("left", "right");
      else if (axis === "x" && position.includes("right")) position = position.replace("right", "left");
      else if (axis === "y" && position.includes("top")) position = position.replace("top", "bottom");
      else if (axis === "y" && position.includes("bottom")) position = position.replace("bottom", "top");

      return position;
    },
    getNextCornerCell(range) {
      const matrix = rootGetters["cells/rangeMatrix"](range);
      let [rowIndex, columnIndex] = getCellIndexInMatrix(matrix, rootState.cells.activeCellId);

      const maxRowIndex = matrix.length - 1;
      const maxColumnIndex = matrix[0].length - 1;

      const corners = [[0, 0]];
      // when there is only one row or one column there are only two corners otherwise four
      if (maxRowIndex === 0) {
        corners.push([0, maxColumnIndex]);
      } else if (maxColumnIndex === 0) {
        corners.push([maxRowIndex, 0]);
      } else {
        corners.push([0, maxColumnIndex], [maxRowIndex, maxColumnIndex], [maxRowIndex, 0]);
      }
      const currentCornerIndex = corners.findIndex((item) => item[0] === rowIndex && item[1] === columnIndex);
      // if active cell is not a corner cell
      if (currentCornerIndex === -1) [rowIndex, columnIndex] = [0, 0];
      else [rowIndex, columnIndex] = corners[(currentCornerIndex + 1) % corners.length];

      return matrix[rowIndex][columnIndex];
    },
    getNextActiveCellInRange(range, rowDelta = 0, columnDelta = 0) {
      let [columnIndex, rowIndex] = rootGetters["cells/getCellCoordinateInRange"](rootState.cells.activeCellId, range);

      const rangeRowLength = range[3] - range[1] + 1;
      const rangeColumnLength = range[2] - range[0] + 1;

      let id;
      do {
        rowIndex += rowDelta;
        columnIndex += columnDelta;

        if (columnIndex > rangeColumnLength - 1) {
          columnIndex = 0;
          rowIndex = (rowIndex + 1) % rangeRowLength;
        } else if (columnIndex < 0) {
          columnIndex = rangeColumnLength - 1;
          rowIndex = rowIndex > 0 ? rowIndex - 1 : rangeRowLength - 1;
        } else if (rowIndex > rangeRowLength - 1) {
          rowIndex = 0;
          columnIndex = (columnIndex + 1) % rangeColumnLength;
        } else if (rowIndex < 0) {
          rowIndex = rangeRowLength - 1;
          columnIndex = columnIndex > 0 ? columnIndex - 1 : rangeColumnLength - 1;
        }
        id = rootGetters["cells/cellIdByIndexInRange"](columnIndex, rowIndex, range);
      } while (
        (rootState.cells.mergedCellIds[id] && rootState.cells.mergedCellIds[id].replace("_merged", "") !== id) ||
        !rootGetters["cells/getCellVisibility"](id)
      );

      return id;
    },
    focusFirstVisibleCell() {
      const firstVisibleColumnIndex = current.design.columns.findIndex((item) =>
        rootGetters["cells/getColumnVisibility"](item.id)
      );
      const firstVisibleRowIndex = current.design.rows.findIndex((item) =>
        rootGetters["cells/getRowVisibility"](item.id)
      );

      const column = current.design.columns[firstVisibleColumnIndex];
      const row = current.design.rows[firstVisibleRowIndex];
      const cellId = getCellId(row.id, column.id);
      const cellIndex = [firstVisibleColumnIndex, firstVisibleRowIndex];

      dispatch(
        "cells/setFormulaRange",
        {
          title: rangeIndexToLabel([...cellIndex, ...cellIndex]),
          activeCellId: cellId,
        },
        { root: true }
      );
      dispatch("cells/setActiveRangeMainCellCoordinate", cellIndex, {
        root: true,
      });
      dispatch("cells/toggleRedrawCanvas", null, { root: true });
    },
  };
};

const main = (context) => {
  const { dispatch, rootGetters, rootState } = context;
  return {
    set(range, activeCellIndex, prevRange, prevActiveCellIndex) {
      dispatch(
        "cells/setFormulaRange",
        {
          title: rangeIndexToLabel(range),
          activeCellId: rootGetters["cells/getCellIdByIndex"](activeCellIndex),
        },
        { root: true }
      );
      dispatch("cells/setActiveRangeMainCellCoordinate", activeCellIndex, { root: true });

      return () => {
        this.set(prevRange, prevActiveCellIndex);
      };
    },
    incrementActiveCellInRange(range, rowDelta, columnDelta) {
      let id = utils(context).getNextActiveCellInRange(range, rowDelta, columnDelta);
      dispatch("cells/setActiveRangeMainCellCoordinate", rootGetters["cells/getCellCoordinate"](id), { root: true });

      dispatch("cells/setActiveCellId", id, { root: true });
      dispatch("cells/initEditCell", id, { root: true });

      return () => {
        this.incrementActiveCellInRange(range, -1 * rowDelta, -1 * columnDelta);
      };
    },

    async setRange({ prevActiveCellId, prevFormulaRange, activeCellId, newRange }) {
      // Undo action
      dispatch("table/setActiveTable", activeCellId, { root: true });
      dispatch("table/setSelectedCellId", activeCellId, { root: true });
      dispatch("cells/setActiveRangeMainCellCoordinate", rootGetters["cells/getCellCoordinate"](activeCellId), {
        root: true,
      });

      if (rootState.cells.rangeDragging?.drag !== "table") {
        dispatch("cells/setFormulaRange", { title: newRange, activeCellId }, { root: true });
      }

      // Undo function
      return async () => {
        this.setRange({ activeCellId: prevActiveCellId, newRange: prevFormulaRange });
      };
    },
    async moveRange({ oldRange, newRange, isFormulaMode }) {
      const newRangeLabel = rangeIndexToLabel(newRange);
      await dispatch("cells/setActiveRangeMainCellCoordinate", [newRange[0], newRange[1]], { root: true });

      if (!isFormulaMode) {
        await dispatch("cells/setFormulaRange", { title: newRangeLabel }, { root: true });
      }

      // Update tables in the range
      const tables = rootGetters["table/allTablesInRange"](unifyRanges(oldRange, newRange));
      for (const element of tables || []) {
        await dispatch("table/updateTableDetails", { updatedTable: element, oldTable: element }, { root: true });
      }

      return async () => {
        await this.moveRange({ oldRange: newRange, newRange: oldRange, isFormulaMode });
      };
    },
  };
};

export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
